import { useEffect, useRef, useState, type ComponentType, type ReactNode } from "react";
import { Link } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useAccount, useTalentWorkspace } from "../../app/session";
import { api } from "../../data";
import { Dialog } from "../../components/ui";
import { toast } from "../../components/Toast";
import { today as todayIn } from "../../lib/format";
import { scopeOf } from "./data";
import { fetchDocument } from "./actions";
import type { EInvoiceExpected } from "@talentkontor/domain";
import { zeigeERechnung } from "../../components/ERechnungAnsicht";
import { isXmlPath } from "../../model/e-rechnung";
import { tr } from "../../lib/sprache";
import type { TimelineStep } from "./model";
import { monthOf, monthShortOf, useLang, useT } from "./sprache";
import { creatorErrorLine, creatorFrameLang, describeCreatorError } from "./fehler";
import { visibilityOf, visibleTimeline, type CreatorVisibility } from "./sicht";
import { pngBlobToDataUrl, SIGNATURE_INK, withDarkInk } from "../agentur/vertraege/tinte";
import "./creator.css";

/** Kleine Bausteine des Creator-Portals mit dem Markup des Design-Pakets. */

/** Arbeitsbereich, Abfrage-Umfang, heutiger Tag in der Zeitzone der Agentur, angemeldete Person und Sprache. */
export function useCreatorContext() {
  const ws = useTalentWorkspace();
  const { account, session } = useAccount();
  const lang = useLang();
  return { ws, scope: scopeOf(ws), today: todayIn(ws.org.timezone), userId: account?.session.userId ?? session?.userId ?? null, lang, account };
}

/**
 * Seite je Talent-Datensatz neu aufbauen: ein Agenturwechsel verwirft Eingaben und Entwürfe der vorigen Agentur
 * (Regel aus dem Rechte-Modell), die Abfragen laden für den neuen Datensatz. Bei einer früheren Agentur (archiviert)
 * gibt es nur Rechnungen und Verträge; andere Seiten erklären das.
 */
export function Scoped({ page: Page, archivedOk }: { page: ComponentType; archivedOk?: boolean }) {
  const ws = useTalentWorkspace();
  if (ws.archived && !archivedOk) return <ArchivedNotice key={ws.link.talent_id} />;
  return <Page key={ws.link.talent_id} />;
}

export function ArchivedNotice({ chat = false }: { chat?: boolean }) {
  const ws = useTalentWorkspace();
  const t = useT();
  return (
    <main id="main" className="content tk-cr">
      <div className="panel stack tk-empty" role="status">
        {/* Überschrift erster Ordnung auch im Archiv (SICHT-CREATOR-09) */}
        <h1>{t(`${ws.org.name} hat dein Profil archiviert.`, `${ws.org.name} has archived your profile.`)}</h1>
        <p>{t("Deine Rechnungen und Verträge bei dieser Agentur bleiben für dich lesbar. Kooperationen, Kalender, Nachrichten und dein Profil dort sind geschlossen.",
          "Your invoices and contracts with this agency remain readable for you. Collaborations, calendar, messages and your profile there are closed.")}</p>
        {/* Befund 27: nur Wege nennen, die es im Archiv gibt */}
        <p>{t("Offene Auszahlung? Öffne die Rechnung und melde über „Zahlung melden“, ob das Geld angekommen ist; ist sie überfällig, frag über „Bei der Agentur nachfragen“ nach, die Antwort kommt per Mail. Eine neue Bankverbindung für offene Auszahlungen und deinen bisherigen Chatverlauf findest du unter „Konto“.",
          "Payout still open? Open the invoice and use “Report payment” to say whether the money has arrived; if it is overdue, use “Ask your agency”, the answer comes by email. You can enter new bank details for open payouts and download your previous chat under “Account”.")}</p>
        {chat && <div className="row"><ChatExportButton talentId={ws.link.talent_id} orgName={ws.org.name} /></div>}
        <div className="row">
          <Link className="btn primary" to="/creator/rechnungen">{t("Zu deinen Rechnungen", "Go to your invoices")}</Link>
          <Link className="btn" to="/creator/vertraege">{t("Zu deinen Verträgen", "Go to your contracts")}</Link>
          <Link className="btn" to="/creator/konto">{t("Zum Konto", "Go to account")}</Link>
        </div>
      </div>
    </main>
  );
}

/** Chatverlauf als Textdatei (Agenturwechsel): auch mit archiviertem Profil, nur lesend. */
export function ChatExportButton({ talentId, orgName }: { talentId: string; orgName: string }) {
  const t = useT();
  const lang = useLang();
  const [busy, setBusy] = useState(false);
  async function run() {
    setBusy(true);
    try {
      const { chatExportText } = await import("./datenExport");
      const text = await chatExportText(api, talentId, orgName, lang);
      downloadText(`${t("Chatverlauf", "Chat")}_${orgName.replace(/[^\p{L}\p{N}]+/gu, "-")}.txt`, text, "text/plain;charset=utf-8");
      toast(t("Chatverlauf gespeichert.", "Chat history saved."));
    } catch (e) {
      toast(creatorErrorLine(e, lang), "alert");
    } finally {
      setBusy(false);
    }
  }
  return (
    <button type="button" onClick={() => void run()} disabled={busy} aria-busy={busy}>
      {busy ? t("Wird vorbereitet …", "Preparing …") : t("Chatverlauf herunterladen", "Download chat history")}
    </button>
  );
}

export function DemoNote() {
  const t = useT();
  return api.mode === "demo" ? <p className="demo-note">{t("Demo · Alle Daten sind erfunden.", "Demo · All data is fictitious.")}</p> : null;
}

// ── Diagramme ──────────────────────────────────────────────────────────────

function niceMax(v: number): number {
  if (v <= 0) return 1000_00;
  const exp = Math.pow(10, Math.floor(Math.log10(v)));
  for (const f of [1, 2, 2.5, 5, 10]) if (f * exp >= v) return f * exp;
  return 10 * exp;
}
function axis(cents: number): string {
  const e = cents / 100;
  return e >= 1000 ? `${new Intl.NumberFormat("de-DE", { maximumFractionDigits: 1 }).format(e / 1000)}k` : String(Math.round(e));
}

/**
 * Monatslinien (.revenue-line): Netto-Anteil bis zum laufenden Monat, danach nur „Voraussichtlich“ gestrichelt (Befund 24:
 * keine Linie, die in der Zukunft auf 0 fällt). Legende mit unterscheidbaren Zeichen (Fläche und Strich). Am Handy statt
 * der verkleinerten Grafik eine Liste mit Balken je Monat in lesbarer Schrift.
 */
export function MonthChart({ months, currency, current }: { months: { month: string; earnedCents: number; pipelineCents: number }[]; currency: string; current?: string }) {
  const lang = useLang();
  const t = useT();
  const monthName = (m: string) => monthOf(m, lang, false);
  const monthShort = (m: string) => monthShortOf(m, lang);
  const max = niceMax(Math.max(0, ...months.map((m) => Math.max(m.earnedCents, m.pipelineCents))));
  const n = months.length;
  const x = (i: number) => (n <= 1 ? 470 : 70 + (i * 800) / (n - 1));
  const y = (v: number) => 205 - (v / max) * 180;
  const every = n > 12 ? Math.ceil(n / 12) : 1;
  const unit = currency === "EUR" ? "Euro" : currency;
  const fmt = (c: number) => new Intl.NumberFormat(lang === "en" ? "en-GB" : "de-DE", { maximumFractionDigits: 0 }).format(c / 100);
  const cur = current ?? months.at(-1)?.month ?? "";
  const past = months.map((m, i) => ({ ...m, i })).filter((m) => m.month <= cur);
  const ahead = months.map((m, i) => ({ ...m, i })).filter((m) => m.month >= cur && (m.pipelineCents > 0 || m.month === cur));
  const hasPipeline = months.some((m) => m.pipelineCents > 0);
  const label = `${t("Dein Netto-Anteil", "Your net share")}: ${past.map((m) => `${monthName(m.month)} ${fmt(m.earnedCents)}`).join(", ")} ${unit}. ${t("Voraussichtlich", "Expected")}: ${months
    .filter((m) => m.pipelineCents > 0).map((m) => `${monthName(m.month)} ${fmt(m.pipelineCents)}`).join(", ") || t("nichts", "nothing")}.`;
  const barMax = Math.max(1, ...months.map((m) => m.earnedCents + m.pipelineCents));
  return (
    <>
      <div className="chart-legend tk-chart-legend">
        <span><i className="tk-dot-earned" aria-hidden="true"></i>{t("Dein Netto-Anteil", "Your net share")}</span>
        {hasPipeline && <span><i className="tk-dot-pipeline" aria-hidden="true"></i>{t("Voraussichtlich (geplant)", "Expected (planned)")}</span>}
      </div>
      <svg className="revenue-line tk-chart-wide" viewBox="0 0 930 250" role="img" aria-label={label}>
        <line className="gridline" x1="55" y1="25" x2="905" y2="25" />
        <text x="3" y="20">{axis(max)}</text>
        <line className="gridline" x1="55" y1="115" x2="905" y2="115" />
        <text x="3" y="110">{axis(max / 2)}</text>
        <line className="gridline" x1="55" y1="205" x2="905" y2="205" />
        <text x="3" y="200">0</text>
        {past.length > 0 && <polyline points={past.map((m) => `${x(m.i).toFixed(1)},${y(m.earnedCents).toFixed(1)}`).join(" ")} fill="none" stroke="currentColor" strokeWidth="3" />}
        {hasPipeline && ahead.length > 0 && (
          <polyline className="tk-line-pipeline" points={ahead.map((m) => `${x(m.i).toFixed(1)},${y(m.month === cur ? m.earnedCents + m.pipelineCents : m.pipelineCents).toFixed(1)}`).join(" ")} fill="none" strokeWidth="2" strokeDasharray="6 5" />
        )}
        {months.map((m, i) => (
          <g key={m.month}>
            {m.month <= cur && <circle cx={x(i)} cy={y(m.earnedCents)} r="4" fill="currentColor" />}
            {m.month > cur && m.pipelineCents > 0 && <circle className="tk-point-pipeline" cx={x(i)} cy={y(m.pipelineCents)} r="4" />}
            {i % every === 0 && <text x={x(i) - 12} y="236">{monthShort(m.month)}</text>}
          </g>
        ))}
      </svg>
      <ul className="tk-chart-bars" aria-label={label}>
        {months.map((m) => (
          <li key={m.month}>
            <span className="tk-bar-month">{monthShort(m.month)}</span>
            <span className="tk-bar-track" aria-hidden="true">
              <span className="tk-bar-earned" style={{ width: `${(m.earnedCents / barMax) * 100}%` }}></span>
              <span className="tk-bar-pipeline" style={{ width: `${(m.pipelineCents / barMax) * 100}%` }}></span>
            </span>
            <span className="tk-bar-value num">{fmt(m.month <= cur ? m.earnedCents : m.pipelineCents)}{m.month > cur && m.pipelineCents > 0 ? ` ${t("geplant", "planned")}` : ""}</span>
          </li>
        ))}
      </ul>
    </>
  );
}

/** Rangliste mit Balken (.rank-row), Breite relativ zum größten Wert. */
export function RankRows({ rows, empty }: { rows: { key: string; label: ReactNode; cents: number; value: string }[]; empty: string }) {
  const max = Math.max(0, ...rows.map((r) => r.cents));
  if (!rows.length) return <p className="muted">{empty}</p>;
  return (
    <>
      {rows.map((r) => (
        <div key={r.key} className="rank-row">
          <span>{r.label}</span>
          <div className="rank-track"><div className="rank-fill" style={{ width: `${max > 0 ? Math.max(2, Math.round((r.cents / max) * 100)) : 0}%` }}></div></div>
          <strong className="num">{r.value}</strong>
        </div>
      ))}
    </>
  );
}

/** Was Creator bei dieser Agentur sehen (Befund 20, 0058). */
export function useCreatorVisibility(): CreatorVisibility {
  return visibilityOf(useTalentWorkspace().org);
}

/** Zeitleiste einer Rechnung; „Zahlung der Marke“ nur, wenn die Agentur ihren Creatorn den Zahlungsstand zeigt (Befund 20). */
export function Timeline({ steps }: { steps: TimelineStep[] }) {
  const visibility = useCreatorVisibility();
  return (
    // Paket 2: als Zeitleiste mit Punkt je Schritt; erledigte Schritte tragen den Akzent
    <ol className="tk-cr-zeitleiste">
      {visibleTimeline(steps, visibility).map((s) => (
        <li key={s.label} className={s.done ? "done" : undefined}>{s.label} · {s.value}</li>
      ))}
    </ol>
  );
}

// ── Dateien ────────────────────────────────────────────────────────────────

/**
 * Datei aus dem privaten Bucket öffnen. Das Fenster wird im Klick geöffnet (sonst sperren Browser es) und
 * bekommt die Datei, sobald sie geladen ist.
 */
export async function openDocument(path: string, fileName?: string, eInvoice?: { sha256?: string | null; erwartet?: EInvoiceExpected | null }) {
  // Steuerprüfung L12: eine E-Rechnung als XML öffnet die Sichtansicht (das XML selbst ist für Menschen nicht lesbar)
  const name = fileName ?? path.split("/").pop() ?? "e-rechnung.xml";
  if ((isXmlPath(path) || eInvoice) && zeigeERechnung({
    title: tr(creatorFrameLang(), "E-Rechnung", "E-invoice"), fileName: name, laden: () => fetchDocument(api, path), sha256: eInvoice?.sha256 ?? null, erwartet: eInvoice?.erwartet ?? null,
  })) return;
  const w = typeof window !== "undefined" ? window.open("", "_blank") : null;
  try {
    const blob = await fetchDocument(api, path);
    const url = URL.createObjectURL(blob);
    if (w) w.location.href = url;
    else {
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName ?? path.split("/").pop() ?? "dokument";
      a.click();
    }
    setTimeout(() => URL.revokeObjectURL(url), 120_000);
  } catch (e) {
    w?.close();
    toast(creatorErrorLine(e, creatorFrameLang()), "alert");
  }
}

/** Datei aus dem privaten Bucket herunterladen (TA-071). */
export async function downloadDocument(path: string, fileName: string) {
  try {
    const blob = await fetchDocument(api, path);
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = fileName;
    a.click();
    setTimeout(() => URL.revokeObjectURL(url), 60_000);
  } catch (e) {
    toast(creatorErrorLine(e, creatorFrameLang()), "alert");
  }
}

/**
 * Schneidet das Auswahlfeld seinen gewählten Text ab? Textbreite gegen die Innenbreite des Felds ohne Innenabstand und
 * Pfeil (SICHT-CREATOR-27). Ohne Messwerte (Server, Test ohne Layout) nie.
 */
export function selectTextCut(textWidth: number, fieldWidth: number, chromePx: number): boolean {
  if (!(textWidth > 0) || !(fieldWidth > 0)) return false;
  return textWidth > fieldWidth - chromePx;
}

/**
 * Gewählter Wert eines Auswahlfelds vollständig unter dem Feld (SICHT-CREATOR-20), aber nur, wenn das Feld ihn tatsächlich
 * abschneidet (SICHT-CREATOR-27: „August 2026“ stand sonst doppelt). Gemessen mit der Schrift des Felds, neu bei jeder
 * Größenänderung.
 */
export function SelectFullText({ selectId, text }: { selectId: string; text: string | null | undefined }) {
  const [cut, setCut] = useState(false);
  useEffect(() => {
    const el = typeof document !== "undefined" ? document.getElementById(selectId) : null;
    if (!el || !text) { setCut(false); return; }
    const canvas = document.createElement("canvas");
    const measure = () => {
      const cs = getComputedStyle(el);
      const g = canvas.getContext("2d");
      if (!g) return;
      g.font = cs.font || `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`;
      // Innenabstand links und rechts plus Platz für den Pfeil der Auswahl
      const chrome = (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0) + 24;
      setCut(selectTextCut(g.measureText(text).width, el.clientWidth, chrome));
    };
    measure();
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [selectId, text]);
  return cut && text ? <small className="tk-select-full" aria-hidden="true">{text}</small> : null;
}

/** Bild aus dem privaten Bucket anzeigen (Unterschrift, Profilbild). */
export function StoredImage({ path, alt, className, ink }: { path: string | null; alt: string; className?: string;
  /** Unterschrift: helle Tinte älterer Fassungen dunkel zeigen, so wie der Vertrag sie übernimmt (SICHT-CREATOR-23) */
  ink?: boolean }) {
  const t = useT();
  const [url, setUrl] = useState<string | null>(null);
  useEffect(() => {
    let alive = true;
    let current: string | null = null;
    if (path) {
      fetchDocument(api, path)
        .then((b) => {
          if (!alive || !b.type.startsWith("image/")) return;
          if (ink && b.type === "image/png") {
            void pngBlobToDataUrl(b).then(withDarkInk).then((u) => { if (alive) setUrl(u); }).catch(() => undefined);
            return;
          }
          current = URL.createObjectURL(b);
          setUrl(current);
        })
        .catch(() => undefined);
    }
    return () => {
      alive = false;
      if (current) URL.revokeObjectURL(current);
      setUrl(null);
    };
  }, [path, ink]);
  if (!path) return null;
  return url ? <img className={className} src={url} alt={alt} /> : <span className="muted">{alt} {t("gespeichert", "saved")}</span>;
}

/** Datei als Download anbieten (CSV-Export). */
export function downloadText(name: string, text: string, type = "text/csv;charset=utf-8") {
  const url = URL.createObjectURL(new Blob([text], { type }));
  const a = document.createElement("a");
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 30_000);
}

// ── Aktionen mit Ladezustand und Fehler in der Sprache des Portals ─────────

/**
 * Wie useAction (components/ui), aber Fehler in der Sprache des Creator-Portals und aus Creator-Sicht (Befunde 2 und 19).
 * Gleiche Form: run, attempt, busy, error.
 */
export function useCreatorAction() {
  const lang = useLang();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const qc = useQueryClient();
  async function run<T>(key: string, fn: () => Promise<T>, success?: string): Promise<T | undefined> {
    setBusy(key);
    setError(null);
    try {
      const r = await fn();
      if (success) toast(success);
      await qc.invalidateQueries({ predicate: (q) => q.queryKey[0] !== "account" });
      return r;
    } catch (e) {
      // Abnahme L12 (E-4): Eingabefehler ohne „Melden“
      if ((e as { melden?: boolean } | null)?.melden === false) {
        setError((e as Error).message);
        toast((e as Error).message, "alert", `creator:${key}`, { melden: false });
        return undefined;
      }
      const d = describeCreatorError(e, lang);
      setError(`${d.title} ${d.text}`.trim());
      toast(creatorErrorLine(e, lang), "alert");
      return undefined;
    } finally {
      setBusy(null);
    }
  }
  async function attempt(key: string, fn: () => Promise<unknown>, success?: string): Promise<boolean> {
    let ok = false;
    await run(key, async () => {
      await fn();
      ok = true;
    }, success);
    return ok;
  }
  return { busy, error, run, attempt, clearError: () => setError(null) };
}

// ── Rückfrage ──────────────────────────────────────────────────────────────

export function ConfirmDialog({ open, title, text, confirm, danger, busy, onConfirm, onClose, children, disabled }: {
  open: boolean;
  title: string;
  text: ReactNode;
  confirm: string;
  danger?: boolean;
  busy?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  children?: ReactNode;
  disabled?: boolean;
}) {
  const t = useT();
  return (
    <Dialog open={open} onClose={onClose} title={title}>
      <p>{text}</p>
      {children}
      <div className="row">
        <button className={danger ? "danger" : "primary"} onClick={onConfirm} disabled={busy || disabled}>{busy ? t("Wird gespeichert …", "Saving …") : confirm}</button>
        <button onClick={onClose} disabled={busy}>{t("Abbrechen", "Cancel")}</button>
      </div>
    </Dialog>
  );
}

// ── Unterschrift zeichnen (TA-106) ─────────────────────────────────────────

/** Zeichenfläche für die Unterschrift; liefert ein PNG. Maus, Stift und Finger über Pointer-Ereignisse. */
export function SignaturePad({ onChange }: { onChange: (png: Blob | null) => void }) {
  const t = useT();
  const ref = useRef<HTMLCanvasElement>(null);
  const drawing = useRef(false);
  const [empty, setEmpty] = useState(true);
  const ctx = () => {
    const c = ref.current;
    const g = c?.getContext("2d");
    if (!c || !g) return null;
    g.lineWidth = 2.5;
    g.lineCap = "round";
    g.lineJoin = "round";
    // feste dunkle Tinte auf fester weißer Fläche (creator.css), unabhängig vom Thema: die Unterschrift landet auf
    // weißem Vertragspapier (SICHT-CREATOR-23; früher die Textfarbe, im Dunkelmodus fast weiß)
    g.strokeStyle = SIGNATURE_INK;
    return { c, g };
  };
  const pos = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const r = e.currentTarget.getBoundingClientRect();
    return { x: ((e.clientX - r.left) / r.width) * e.currentTarget.width, y: ((e.clientY - r.top) / r.height) * e.currentTarget.height };
  };
  const finish = () => {
    if (!drawing.current) return;
    drawing.current = false;
    ref.current?.toBlob((b) => onChange(b), "image/png");
  };
  const clear = () => {
    const k = ctx();
    if (!k) return;
    k.g.clearRect(0, 0, k.c.width, k.c.height);
    setEmpty(true);
    onChange(null);
  };
  return (
    <div className="stack tk-gap-sm">
      <canvas
        ref={ref}
        className="tk-signature-pad"
        width={600}
        height={180}
        aria-label={t("Unterschrift zeichnen", "Draw signature")}
        onPointerDown={(e) => {
          const k = ctx();
          if (!k) return;
          e.currentTarget.setPointerCapture(e.pointerId);
          drawing.current = true;
          const p = pos(e);
          k.g.beginPath();
          k.g.moveTo(p.x, p.y);
          setEmpty(false);
        }}
        onPointerMove={(e) => {
          if (!drawing.current) return;
          const k = ctx();
          if (!k) return;
          const p = pos(e);
          k.g.lineTo(p.x, p.y);
          k.g.stroke();
        }}
        onPointerUp={finish}
        onPointerLeave={finish}
      />
      <div className="row">
        <button type="button" onClick={clear} disabled={empty}>{t("Zeichnung löschen", "Clear drawing")}</button>
        <small>{empty ? t("Unterschreibe mit Maus, Stift oder Finger.", "Sign with mouse, pen or finger.") : t("Gezeichnet. Speichern übernimmt die Zeichnung.", "Drawn. Saving uses the drawing.")}</small>
      </div>
    </div>
  );
}
